import React from "react";
import { withTranslation } from "react-i18next";
import produce from "immer";
import { compose } from "recompose";

import debug from "src/modules/debug-logger";
import { RPCRequests, Message } from "src/modules/communication-protobuf";
import { withParentStation } from "src/modules/parent-station/ParentStation/Context";
import {
  isMediaEvent,
  isVideoEvent,
  isPhotoEvent
} from "src/modules/activity-log/supported-events";
import { withMessenger } from "src/modules/messenger/Provider";
import GenericFailureDialog from "src/components/GenericFailureDialog";
import {
  getEventThumbnailDataUrl,
  getSnapshotSignedUrl,
  getVideoSignedUrl,
  getDataForMediaUrl,
  downloadEventMedia
} from "src/modules/activity-log/ajax/cloud";

import normalizeEvent from "../normalizeEvent";
import CapturedMedia from "../components/CapturedMedia";
import LiveTimeline from "./LiveTimeline";
import Drawer from "./Drawer";
import { DarkMode } from "src/styling/Modes";

const initialState = {
  loading: true,
  refreshing: false,
  hasGenericFailure: false,
  session: null,
  inProgressEvent: null,
  nextUpdateNumber: null,
  openedEvent: null,
  renderFreemiumFlow: false
};

class LiveActivityLog extends React.Component {
  state = initialState;
  didUnmount = false;
  fetchThumbnailTimeoutsIds = {};
  fetchThumbnailAttempts = {};

  componentDidMount() {
    this.startActivityLog();
  }

  componentWillUnmount() {
    this.didUnmount = true;
    this.stopActivityLog();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.parentStation.primaryStation.jid !==
      this.props.parentStation.primaryStation.jid
    ) {
      this.handleStationChange();
    }
  }

  async handleStationChange() {
    await this.stopActivityLog();
    await this.startActivityLog();
  }

  startActivityLog = async () => {
    const { parentStation, messenger } = this.props;
    const { primaryStation } = parentStation;
    const { rpc } = primaryStation;
    const request = RPCRequests.StartActivityLog.create();
    try {
      const { inProgressEvent, session, nextUpdateNumber } = await rpc.call(
        request,
        primaryStation.jid
      );
      session.events.forEach(normalizeEvent);
      session.events.reverse();
      this.setState(
        {
          inProgressEvent,
          session,
          nextUpdateNumber
        },
        () => {
          this.fetchEventsThumbnails();
        }
      );
      messenger.on(
        Message.ActivityLogUpdateMessage.name,
        this.handleActivityLogUpdateMessage
      );
      this.loadTimeoutId = setTimeout(() => {
        this.setState({ loading: false });
      }, 500);
    } catch (error) {
      debug.activityLog(error);
      this.setState({ hasGenericFailure: true });
      return;
    }
  };

  stopActivityLog = async () => {
    const { parentStation, messenger } = this.props;
    const { primaryStation } = parentStation;
    const { rpc } = primaryStation;

    if (!this.didUnmount) {
      this.setState(initialState);
    }
    this.onStopAndRefresh();

    clearTimeout(this.loadTimeoutId);
    messenger.removeListener(
      Message.ActivityLogUpdateMessage.name,
      this.handleActivityLogUpdateMessage
    );
    if (primaryStation?.jid) {
      try {
        await rpc.call(
          RPCRequests.StopActivityLog.create(),
          primaryStation.jid
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log("err: ", error);
      }
    }
  };

  onStopAndRefresh = () => {
    Object.values(this.fetchThumbnailTimeoutsIds).forEach((id) =>
      clearTimeout(id)
    );
    this.fetchThumbnailTimeoutsIds = {};
    this.fetchThumbnailAttempts = {};
    this.revokeThumbnailsDataUrls();
  };

  revokeThumbnailsDataUrls = () => {
    const { session } = this.state;
    if (!session) return;
    session.events.forEach((event) => {
      if (event.thumbnailSrc) {
        URL.revokeObjectURL(event.thumbnailSrc);
      }
      if (event.mediaDataUrl) {
        URL.revokeObjectURL(event.mediaDataUrl);
      }
    });
  };

  handleActivityLogUpdateMessage = (message, from) => {
    const { updateNumber, eventToCreate, eventToUpdate, eventToDelete } =
      message;
    const { nextUpdateNumber, refreshing } = this.state;

    if (refreshing) return;
    if (updateNumber !== nextUpdateNumber) {
      this.refresh();
      return;
    }

    if (eventToCreate) normalizeEvent(eventToCreate);
    if (eventToUpdate) normalizeEvent(eventToUpdate);
    if (eventToDelete) normalizeEvent(eventToDelete);

    if (eventToDelete) {
      this.removeEventFromState(eventToDelete);
    }

    if (
      eventToUpdate?.eventType === "AWAKE" ||
      eventToUpdate?.eventType === "SLEEPING"
    ) {
      this.setState(
        produce((draft) => {
          draft.session.events.unshift(eventToUpdate);
        })
      );
    }

    if (
      eventToCreate?.eventType === "AWAKE" ||
      eventToCreate?.eventType === "SLEEPING"
    ) {
      this.setState(
        produce((draft) => {
          draft.inProgressEvent = eventToCreate;
        })
      );
    }

    if (eventToCreate && isMediaEvent(eventToCreate.eventType)) {
      this.setState(
        produce((draft) => {
          draft.session.events.unshift(eventToCreate);
        })
      );
      this.fetchEventThumbnail(eventToCreate);
    }

    this.setState(
      produce((draft) => {
        draft.nextUpdateNumber += 1;
      })
    );
  };

  async refresh() {
    const { parentStation } = this.props;
    const { primaryStation } = parentStation;
    const { rpc } = primaryStation;

    this.setState({ refreshing: true });
    this.onStopAndRefresh();

    const request = RPCRequests.RefreshActivityLog.create();
    try {
      const { inProgressEvent, session, nextUpdateNumber } = await rpc.call(
        request,
        primaryStation.jid
      );
      session.events.forEach(normalizeEvent);
      session.events.reverse();
      this.setState(
        {
          inProgressEvent,
          session,
          nextUpdateNumber,
          refreshing: false,
          loading: false
        },
        this.fetchEventsThumbnails
      );
    } catch (error) {
      this.setState({ hasGenericFailure: true });
      return;
    }
  }

  fetchEventThumbnail = async (event) => {
    if (isMediaEvent(event.eventType)) {
      const attemptNumber = this.fetchThumbnailAttempts[event.id] || 0;
      this.fetchThumbnailAttempts[event.id] = attemptNumber + 1;

      const isMovingPreviewAvailable = event.eventType.includes("VIDEO");
      const fileExtension = isMovingPreviewAvailable ? "webp" : "jpg";

      try {
        const dataUrl = await getEventThumbnailDataUrl(
          event.fileName.value,
          fileExtension
        );

        this.setState(
          produce((draftState) => {
            const currentEvent = this.getEventForId(event.id, draftState);
            if (currentEvent) {
              currentEvent.thumbnailSrc = dataUrl;
            }
          })
        );
      } catch (error) {
        debug.activityLog("fetchEventThumbnail failed", error);

        if (attemptNumber === 5 || this.didUnmount) return;
        this.fetchThumbnailTimeoutsIds[event.id] = setTimeout(() => {
          this.fetchEventThumbnail(event);
        }, 2000 * attemptNumber);
      }
    }
  };
  fetchEventsThumbnails = () => {
    const { session } = this.state;
    session.events.forEach(this.fetchEventThumbnail);
  };

  getEventForId = (id, state = this.state) => {
    if (id == null) return null;
    return state.session?.events.find((event) => event.id === id);
  };

  openEventView = async (eventId) => {
    const event = this.getEventForId(eventId);
    this.setState({
      openedEvent: {
        id: eventId,
        signedUrl: event.mediaDataUrl
      }
    });
    if (event.mediaDataUrl) return;

    try {
      if (isPhotoEvent(event.eventType)) {
        const signedUrl = await getSnapshotSignedUrl(event.fileName.value);
        await this.cacheMediaAsDataUrl({
          eventId,
          url: signedUrl
        });
        return;
      }

      if (isVideoEvent(event.eventType)) {
        const signedUrl = await getVideoSignedUrl(event.fileName.value);
        if (this.state.openedEvent?.id !== eventId) {
          return;
        }
        this.setState(({ openedEvent }) => ({
          openedEvent: { ...openedEvent, signedUrl }
        }));

        try {
          await this.cacheMediaAsDataUrl({
            eventId,
            url: signedUrl
          });
        } catch (error) {
          debug.activityLog("cacheMediaAsDataUrl failed", error);
        }
      }
    } catch (error) {
      debug.activityLog("openEventView failed", error);
      this.setState({ hasGenericFailure: true, openedEvent: null });
    }
  };

  cacheMediaAsDataUrl = async ({ eventId, url }) => {
    const event = this.getEventForId(eventId);
    if (!event) return;
    const data = await getDataForMediaUrl(url);
    this.setState(
      produce((draftState) => {
        const currentEvent = this.getEventForId(eventId, draftState);
        if (currentEvent) {
          currentEvent.mediaDataUrl = data.dataUrl;
          currentEvent.mediaBlob = data.blob;
        }
      })
    );
  };

  removeEventFromState = (eventToDelete) => {
    this.setState(
      produce((draftState) => {
        if (draftState.openedEvent?.id === eventToDelete.id) {
          draftState.openedEvent = null;
        }
        draftState.session.events = draftState.session.events.filter(
          (event) => event.id !== eventToDelete.id
        );
      })
    );
    URL.revokeObjectURL(eventToDelete.thumbnailSrc);
    URL.revokeObjectURL(eventToDelete.mediaDataUrl);
  };

  deleteEvent = async (eventId) => {
    const eventToDelete = this.getEventForId(eventId);
    const { primaryStation } = this.props.parentStation;
    const { rpc } = primaryStation;
    const request = RPCRequests.DeleteActivityLogEvent.create(eventId);
    try {
      await rpc.call(request, primaryStation.jid);
      this.removeEventFromState(eventToDelete);
    } catch (error) {
      debug.activityLog("deleteEvent failed:", error);
      this.setState({ hasGenericFailure: true, openedEvent: null });
    }
  };

  handleEventMediaDownload = async (eventId) => {
    try {
      const event = this.getEventForId(eventId);
      await downloadEventMedia({
        eventType: event.eventType,
        fileName: event.fileName.value,
        mediaBlob: event.mediaBlob,
        captureDate:
          new Date(this.props.monitoringStartTime).getTime() + event.start
      });
    } catch (error) {
      debug.activityLog("handleEventMediaDownload failed: ", error);
    }
  };

  render() {
    const { session, loading, hasGenericFailure, refreshing, openedEvent } =
      this.state;
    const { parentStation, onClose, isOpen, togglePurchaseFlow } = this.props;
    const mode = parentStation.primaryStation.mode;
    const monitoredAvatar = parentStation.getMonitoredAvatar();
    const originalOpenedEvent = openedEvent
      ? this.getEventForId(openedEvent.id)
      : null;

    return (
      <>
        <DarkMode active={mode === "video"} w="100%" h="100%">
          <LiveTimeline
            toggleFreemiumFlow={togglePurchaseFlow}
            isOpen={isOpen}
            mode={mode}
            loading={loading || refreshing}
            monitoredAvatar={monitoredAvatar}
            session={session}
            openEventView={this.openEventView}
            fetchEventThumbnail={this.fetchEventThumbnail}
          />
        </DarkMode>

        <CapturedMedia
          videoThumbnailSrc={originalOpenedEvent?.thumbnailSrc}
          isOpen={Boolean(openedEvent)}
          onClose={() => this.setState({ openedEvent: null })}
          type={originalOpenedEvent?.eventType}
          src={openedEvent?.signedUrl || originalOpenedEvent?.mediaDataUrl}
          ratio={originalOpenedEvent?.mediaRatio}
          onDelete={() => this.deleteEvent(originalOpenedEvent?.id)}
          onDownload={() =>
            this.handleEventMediaDownload(originalOpenedEvent?.id)
          }
          autoplay={true}
        />
        <GenericFailureDialog
          isOpen={hasGenericFailure}
          onDismiss={() => {
            if (this.state.loading || this.state.refreshing) {
              onClose();
              return;
            }
            this.setState({ hasGenericFailure: false });
          }}
        />
      </>
    );
  }
}

const enhance = compose(withParentStation, withMessenger, withTranslation());

function LiveActivityLogInDrawer({ isOpen, mode, ...rest }) {
  return (
    <Drawer isOpen={isOpen} mode={mode}>
      {isOpen && <LiveActivityLog mode={mode} {...rest} />}
    </Drawer>
  );
}

export default enhance(LiveActivityLogInDrawer);
